import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment.development';
import * as CryptoJS from 'crypto-js';
import { CookieService } from 'ngx-cookie-service'; // ✅ لازم يكون موجود
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private readonly TOKEN_KEY = 'au52th2t6en';
  private readonly SECRET_KEY = environment.secretKey;
  private readonly AVATAR_KEY = 'user_avatar_override'; // مفتاح تخزين الصورة المحدثة (في cookies)
  
  // Observable للتحقق من حالة التوكين
  private tokenSubject: BehaviorSubject<boolean>;
  public token$: Observable<boolean>;

  // Observable لتحديث صورة المستخدم
  private userAvatarSubject = new BehaviorSubject<string | null>(null);
  public userAvatar$ = this.userAvatarSubject.asObservable();

  constructor(private cookieService: CookieService) {
    this.tokenSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
    this.token$ = this.tokenSubject.asObservable();
    // مراقبة التغييرات في الكوكيز كل ثانية
    this.startTokenWatcher();
    // تحميل الصورة المحفوظة عند بدء التشغيل
    this.loadSavedAvatar();
  }

  /**
   * 🖼️ تحميل الصورة المحفوظة من cookies
   */
  private loadSavedAvatar(): void {
    const savedAvatar = this.cookieService.get(this.AVATAR_KEY);
    if (savedAvatar) {
      this.userAvatarSubject.next(savedAvatar);
    }
  }

  /**
   * 👁️ مراقبة حالة التوكين والـ cookies المهمة
   * لو اتمسحت أي cookie مهمة → logout تلقائي
   */
  private startTokenWatcher(): void {
    setInterval(() => {
      // مراقبة التوكين
      const hasToken = this.isAuthenticated();
      if (this.tokenSubject.value !== hasToken) {
        this.tokenSubject.next(hasToken);
      }
      
      // مراقبة الـ avatar cookie - لو اتمسحت وكان المستخدم مسجل دخول
      if (hasToken) {
        const hasAvatarCookie = this.cookieService.check(this.AVATAR_KEY);
        // لو الـ avatar cookie كانت موجودة قبل كده واتمسحت
        if (this.userAvatarSubject.value && !hasAvatarCookie) {
          // مسح كل حاجة وعمل logout
          this.forceLogout();
        }
      }
    }, 1000);
  }

  /**
   * 🚪 إجبار المستخدم على تسجيل الخروج
   */
  private forceLogout(): void {
    this.fullCleanup();
    // إعادة تحميل الصفحة للخروج من التطبيق
    window.location.href = '/login';
  }

  /**
   * ✅ حفظ التوكين بعد تشفيره في الـ cookies
   */
  saveToken(token: string): void {
    const encrypted = CryptoJS.AES.encrypt(token, this.SECRET_KEY).toString();

    // حفظ في الكوكيز مع إعدادات الأمان
    this.cookieService.set(this.TOKEN_KEY, encrypted, {
      expires: 1,          // صلاحية يوم واحد
      sameSite: 'Lax',     // يمنع التسريب عبر مواقع أخرى
      secure: false,       // اسمح بـ HTTP للتطوير
      path: '/'            // متاح على مستوى التطبيق كله
    });
  }

  /**
   * 🔓 فك تشفير التوكين عند قراءته
   */
  getToken(): string | null {
    const encrypted = this.cookieService.get(this.TOKEN_KEY);
    if (!encrypted) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, this.SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || null;
    } catch (err) {
      return null;
    }
  }

  /**
   * ❌ حذف التوكين (عند تسجيل الخروج)
   */
  clearToken(): void {
    const expired = new Date(0);
    // مسح التوكين
    this.cookieService.set(this.TOKEN_KEY, '', { expires: expired, path: '/', sameSite: 'Lax', secure: false });
    // مسح الصورة
    this.cookieService.set(this.AVATAR_KEY, '', { expires: expired, path: '/', sameSite: 'Lax', secure: false });
    
    // تأكيد المسح بالطرق الأخرى للاحتياط
    this.cookieService.delete(this.TOKEN_KEY, '/');
    this.cookieService.delete(this.AVATAR_KEY, '/');
    
    this.userAvatarSubject.next(null);
  }

  /**
   * 🧹 مسح كل البيانات الحساسة كما طلب المستخدم (Logout)
   */
  fullCleanup(): void {
    // 1. مسح الكوكيز (التوكين + الصورة)
    this.clearToken();
    this.clearUserAvatar();

    // 2. مسح الـ localStorage مع الحفاظ فقط على بيانات التتبع التقنية
    const activeTabsMap = localStorage.getItem('active_tabs_map');
    localStorage.clear();
    if (activeTabsMap) {
      localStorage.setItem('active_tabs_map', activeTabsMap);
    }

    // 3. مسح الـ sessionStorage (مع الحفاظ على الـ tab_id الحالي)
    const tabId = sessionStorage.getItem('tab_id');
    sessionStorage.clear();
    if (tabId) {
      sessionStorage.setItem('tab_id', tabId);
    }
  }

  /**
   * �🧠 التحقق من وجود توكين صالح
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token; // موجود = true
  }

  /**
   * 🔐 فك تشفير JWT Token واستخراج البيانات منه (UTF-8 Safe for Arabic)
   */
  decodeToken(): any {
    const token = this.getToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // تحويل Base64URL إلى Base64 العادي
      const payload = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // فك التشفير بطريقة آمنة للـ UTF-8 (يدعم العربي)
      const decodedPayload = decodeURIComponent(
        atob(payload)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(decodedPayload);
    } catch (error) {
      return null;
    }
  }

  /**
   * 📋 الحصول على معلومة محددة من التوكين
   */
  getTokenData(key: string): any {
    const decoded = this.decodeToken();
    return decoded ? decoded[key] : null;
  }

  /**
   * ⏰ التحقق من انتهاء صلاحية التوكين
   */
  isTokenExpired(): boolean {
    const decoded = this.decodeToken();
    if (!decoded || !decoded.exp) return true;
    
    const expirationDate = decoded.exp * 1000; // تحويل لـ milliseconds
    return Date.now() >= expirationDate;
  }
  /**
   * 🖼️ تحديث صورة المستخدم وحفظها في cookies
   */
  updateUserAvatar(avatarUrl: string | null): void {
    if (avatarUrl) {
      // حفظ في الكوكيز مع نفس إعدادات الأمان
      this.cookieService.set(this.AVATAR_KEY, avatarUrl, {
        expires: 30,         // صلاحية 30 يوم
        sameSite: 'Lax',
        secure: false,
        path: '/'
      });
    } else {
      const expired = new Date(0);
      this.cookieService.set(this.AVATAR_KEY, '', { expires: expired, path: '/', sameSite: 'Lax', secure: false });
      this.cookieService.delete(this.AVATAR_KEY, '/');
    }
    this.userAvatarSubject.next(avatarUrl);
  }

  /**
   * 🖼️ الحصول على صورة المستخدم (من cookies أو من التوكين)
   */
  getUserAvatar(): string | null {
    // أولاً نشوف لو في صورة محفوظة في cookies
    const savedAvatar = this.cookieService.get(this.AVATAR_KEY);
    if (savedAvatar) {
      return savedAvatar;
    }
    // لو مفيش، نرجع الصورة من التوكين
    return this.getTokenData('profilePicturePath');
  }

  /**
   * 🧹 مسح صورة المستخدم المحفوظة (عند logout)
   */
  clearUserAvatar(): void {
    const expired = new Date(0);
    this.cookieService.set(this.AVATAR_KEY, '', { expires: expired, path: '/', sameSite: 'Lax', secure: false });
    this.cookieService.delete(this.AVATAR_KEY, '/');
    this.userAvatarSubject.next(null);
  }
}
